import { Link } from "react-router-dom";
import {
    ArrowRight,
    Compass,
    ShieldCheck,
    Users,
    Hammer,
    Award,
    Building2,
    Home as HomeIcon,
    HardHat,
    ClipboardCheck,
    BadgeCheck,
    Quote,
} from "lucide-react";
import FadeUp from "../components/FadeUp";
import { STOCK } from "../lib/stock";
import { FRANK_PHOTO, FEATURED_PROJECT_COMMERCIAL, LOGO_URL } from "../lib/constants";

const VALUES = [
    {
        icon: ShieldCheck,
        title: "Trust",
        body: "Transparent scopes, clean reporting, and honest timelines. No surprises mid-project.",
    },
    {
        icon: Compass,
        title: "Strategy",
        body: "We weigh every choice against return. Finish, schedule, and scope all serve the outcome.",
    },
    {
        icon: Users,
        title: "Partnership",
        body: "Owners, agents, and investors get one PM, one schedule, and one accountable team.",
    },
    {
        icon: Hammer,
        title: "Craft",
        body: "Technical precision and quality control on every trade, from rough-in to final punch.",
    },
];

const CAPABILITIES = [
    { icon: Award, title: "20+", sub: "Years Experience" },
    { icon: Building2, title: "Commercial", sub: "Buildouts & Tenant Improvements" },
    { icon: HomeIcon, title: "Residential", sub: "Remodeling & Additions" },
    { icon: HardHat, title: "Ground-Up", sub: "Construction" },
    { icon: ClipboardCheck, title: "Project Management", sub: "& Owner Representation" },
    { icon: BadgeCheck, title: "Licensed & Insured", sub: "" },
];

const FRANK_FEATURED = [
    {
        image: FEATURED_PROJECT_COMMERCIAL,
        eyebrow: "Commercial Construction",
        title: "Domino's Retail Buildout",
        icon: Building2,
    },
    {
        image:
            "/stock/unsplash-1565538810643.jpg",
        eyebrow: "Custom Residential",
        title: "Luxury Kitchen Renovation",
        icon: HomeIcon,
    },
    {
        image:
            "/stock/pexels-26872298.jpg",
        eyebrow: "Structural Construction",
        title: "Steel Building Development",
        icon: Building2,
    },
];

export default function About() {
    return (
        <main data-testid="about-page" className="bg-[#050505]">
            {/* HERO */}
            <section className="relative pt-40 pb-24 overflow-hidden">
                <div className="absolute inset-0">
                    <img
                        src={STOCK.aboutHero}
                        alt=""
                        aria-hidden="true"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/85 to-[#050505]/30" />
                    <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/70 via-transparent to-[#050505]" />
                </div>
                <div className="container-bevel relative z-10">
                    <FadeUp>
                        <span className="overline">About Bevel</span>
                    </FadeUp>
                    <FadeUp delay={1}>
                        <h1 className="font-display text-5xl md:text-7xl mt-6 leading-[0.95] max-w-4xl">
                            Bridging architectural vision and{" "}
                            <span className="text-[#F5A623]">
                                structural reality.
                            </span>
                        </h1>
                    </FadeUp>
                    <FadeUp delay={2}>
                        <p className="mt-8 text-lg text-white/65 max-w-2xl leading-relaxed">
                            Bevel Commercial Services is a Tulsa-based
                            commercial and residential renovation partner. We
                            manage every complex phase of construction with the
                            technical precision and local expertise serious
                            property owners require.
                        </p>
                    </FadeUp>
                </div>
            </section>

            {/* MISSION */}
            <section className="section-pad bg-[#080808] hairline-top">
                <div className="container-bevel grid grid-cols-1 md:grid-cols-12 gap-12">
                    <div className="md:col-span-5">
                        <FadeUp>
                            <span className="overline">Our Mission</span>
                            <h2 className="font-display text-4xl md:text-5xl mt-4 leading-tight">
                                Renovation as a financial decision.
                            </h2>
                        </FadeUp>
                    </div>
                    <div className="md:col-span-7 space-y-5 text-white/65 leading-relaxed">
                        <FadeUp delay={1}>
                            <p>
                                We exist to help realtors, investors, and
                                homeowners convert renovation spend into
                                measurable property value. Every Bevel project
                                is scoped, scheduled, and built around an
                                outcome: sale price, refinance value, or
                                resale-ready handover.
                            </p>
                        </FadeUp>
                        <FadeUp delay={2}>
                            <p>
                                That mindset shapes how we work: tight cost
                                discipline, weekly reporting, and a single
                                project manager owning the entire build from
                                first walkthrough to final punch list.
                            </p>
                        </FadeUp>
                    </div>
                </div>
            </section>

            {/* VALUES */}
            <section
                className="section-pad"
                data-testid="about-values-section"
            >
                <div className="container-bevel">
                    <FadeUp>
                        <span className="overline">Our Values</span>
                        <h2 className="font-display text-4xl md:text-5xl mt-4 leading-tight">
                            Built on four principles.
                        </h2>
                    </FadeUp>
                    <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
                        {VALUES.map((v, i) => (
                            <FadeUp
                                key={v.title}
                                delay={i}
                                className="bg-[#0A0A0A] p-8"
                            >
                                <v.icon
                                    size={26}
                                    className="text-[#F5A623]"
                                    strokeWidth={1.5}
                                />
                                <h3 className="font-display text-xl mt-5 text-white">
                                    {v.title}
                                </h3>
                                <p className="text-white/60 text-sm mt-3 leading-relaxed">
                                    {v.body}
                                </p>
                            </FadeUp>
                        ))}
                    </div>
                </div>
            </section>

            {/* LEADERSHIP / FRANK BIO */}
            <section
                className="relative overflow-hidden"
                data-testid="about-leadership-section"
            >
                {/* Hero portrait — text left, full portrait right */}
                <div className="relative bg-[#050505] border-t border-white/10 overflow-hidden">
                    <div className="container-bevel grid grid-cols-1 lg:grid-cols-12 gap-0 items-stretch">
                        <div className="lg:col-span-6 relative z-10 py-16 md:py-20 lg:py-28 lg:pr-10 order-2 lg:order-1">
                            <FadeUp>
                                <img
                                    src={LOGO_URL}
                                    alt="Bevel Commercial Services"
                                    className="w-56 md:w-64 lg:w-80 h-auto mb-10 select-none"
                                    data-testid="frank-bio-logo"
                                    draggable="false"
                                />
                                <span className="block text-[11px] md:text-xs font-bold uppercase tracking-[0.25em] text-[#F5A623] leading-relaxed">
                                    Building Excellence.
                                    <br />
                                    Delivering Results.
                                </span>
                                <h2 className="font-display uppercase tracking-tight text-5xl md:text-6xl lg:text-7xl mt-8 leading-[0.95] text-white">
                                    Meet
                                    <br />
                                    Frank Smalls
                                </h2>
                                <p className="mt-8 text-[11px] md:text-xs uppercase tracking-[0.28em] text-white/75 font-semibold">
                                    Owner&nbsp;&nbsp;|&nbsp;&nbsp;General
                                    Contractor
                                </p>
                                <p className="mt-2 text-[11px] md:text-xs uppercase tracking-[0.28em] text-[#1f7ee0] font-bold">
                                    Commercial &amp; Residential Construction
                                </p>
                                <p className="mt-7 text-base md:text-lg text-white/75 max-w-md leading-relaxed">
                                    With over 20 years of experience delivering
                                    commercial buildouts, renovations, custom
                                    construction, and facility improvements
                                    throughout Oklahoma.
                                </p>
                            </FadeUp>
                        </div>
                        <div className="lg:col-span-6 relative order-1 lg:order-2 min-h-[420px] sm:min-h-[520px] lg:min-h-[720px]">
                            <img
                                src={FRANK_PHOTO}
                                alt="Frank Smalls, Founder of Bevel Commercial Services"
                                className="absolute inset-0 w-full h-full object-cover object-[center_15%]"
                                data-testid="frank-portrait"
                            />
                            {/* Soft fade on the left edge to blend into the dark text panel */}
                            <div className="hidden lg:block absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-[#050505] to-transparent pointer-events-none" />
                        </div>
                    </div>
                </div>

                {/* About Frank + Capability sidebar */}
                <div className="bg-[#080808] border-t border-white/10">
                    <div className="container-bevel section-pad grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-0">
                        <FadeUp className="lg:col-span-7 lg:pr-14">
                            <div className="flex items-center gap-3">
                                <span className="block h-px w-8 bg-[#F5A623]" />
                                <span className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#1f7ee0]">
                                    About Frank
                                </span>
                            </div>
                            <h3 className="font-display text-3xl md:text-5xl mt-5 leading-tight text-white">
                                Construction Expertise
                                <br />
                                Built on Integrity
                            </h3>
                            <div className="mt-8 space-y-5 text-white/70 leading-relaxed">
                                <p>
                                    For more than two decades, Frank Smalls has
                                    helped homeowners, business owners, and
                                    developers bring their vision to life
                                    through quality craftsmanship and
                                    dependable project management.
                                </p>
                                <p>
                                    As founder of Bevel Commercial Services,
                                    Frank remains actively involved in every
                                    project—from initial planning and budgeting
                                    through final completion. His commitment to
                                    communication, accountability, and
                                    attention to detail has earned the trust of
                                    clients across Oklahoma.
                                </p>
                                <p>
                                    Whether leading a commercial buildout,
                                    overseeing a large-scale renovation, or
                                    managing a custom construction project,
                                    Frank&apos;s focus remains the same: deliver
                                    exceptional results on schedule, within
                                    budget, and built to last.
                                </p>
                            </div>
                        </FadeUp>

                        <FadeUp
                            delay={1}
                            className="lg:col-span-5 lg:pl-14 lg:border-l lg:border-white/10"
                        >
                            <ul className="divide-y divide-white/10">
                                {CAPABILITIES.map((s) => (
                                    <li
                                        key={s.title}
                                        className="flex items-center gap-6 py-5 first:pt-0 last:pb-0"
                                    >
                                        <s.icon
                                            size={32}
                                            className="text-[#F5A623] shrink-0"
                                            strokeWidth={1.5}
                                        />
                                        <div className="leading-tight">
                                            <div className="text-base md:text-lg font-bold uppercase tracking-[0.12em] text-white">
                                                {s.title}
                                            </div>
                                            {s.sub && (
                                                <div className="text-[11px] uppercase tracking-[0.18em] text-white/65 mt-1.5 font-medium">
                                                    {s.sub}
                                                </div>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </FadeUp>
                    </div>
                </div>

                {/* Featured projects */}
                <div className="bg-[#050505] border-t border-white/10">
                    <div className="container-bevel section-pad">
                        <FadeUp className="text-center">
                            <span className="text-xs font-bold uppercase tracking-[0.3em] text-[#005BBB]">
                                Featured Projects
                            </span>
                            <h3 className="font-display text-3xl md:text-5xl mt-4 leading-tight text-white">
                                Quality Work. Lasting Impact.
                            </h3>
                        </FadeUp>

                        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
                            {FRANK_FEATURED.map((p, i) => (
                                <FadeUp
                                    key={p.title}
                                    delay={i}
                                    className="group border border-white/10 bg-[#0A0A0A] hover:border-[#F5A623]/40 transition-colors"
                                    data-testid={`frank-featured-${i}`}
                                >
                                    <div className="relative aspect-[4/3] overflow-hidden">
                                        <img
                                            src={p.image}
                                            alt={p.title}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                        />
                                    </div>
                                    <div className="p-5 border-t border-white/10 flex items-start gap-3">
                                        <p.icon
                                            size={20}
                                            className="text-[#F5A623] mt-1 shrink-0"
                                            strokeWidth={1.5}
                                        />
                                        <div>
                                            <div className="text-[10px] uppercase tracking-[0.25em] text-[#005BBB] font-bold">
                                                {p.eyebrow}
                                            </div>
                                            <div className="font-display text-lg text-white mt-1">
                                                {p.title}
                                            </div>
                                        </div>
                                    </div>
                                </FadeUp>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Quote + signature */}
                <div className="bg-[#080808] border-t border-white/10">
                    <div className="container-bevel py-16 md:py-20 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
                        <FadeUp className="md:col-span-7">
                            <Quote
                                size={32}
                                className="text-[#F5A623]"
                                strokeWidth={1.5}
                            />
                            <p className="font-display text-2xl md:text-3xl text-white mt-5 leading-snug">
                                Our success is measured by the trust our
                                clients place in us and the quality of the
                                projects we leave behind.
                            </p>
                        </FadeUp>
                        <FadeUp
                            delay={1}
                            className="md:col-span-5 md:text-right"
                        >
                            <div
                                className="text-4xl md:text-5xl text-[#F5A623]"
                                style={{
                                    fontFamily:
                                        '"Brush Script MT", "Lucida Handwriting", cursive',
                                }}
                            >
                                Frank Smalls
                            </div>
                            <p className="mt-3 text-xs uppercase tracking-[0.22em] text-white/75 font-semibold">
                                Founder &amp; General Contractor
                            </p>
                            <p className="mt-1 text-xs uppercase tracking-[0.22em] text-[#005BBB]">
                                Bevel Commercial Services
                            </p>
                        </FadeUp>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="section-pad bg-[#080808] hairline-top">
                <div className="container-bevel">
                    <FadeUp>
                        <div className="border border-white/10 bg-[#0A0A0A] p-10 md:p-16 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
                            <div className="md:col-span-8">
                                <h2 className="font-display text-3xl md:text-5xl leading-tight">
                                    Ready to elevate your space?
                                </h2>
                                <p className="text-white/60 mt-5 max-w-xl">
                                    Share your project details. We&apos;ll respond
                                    within 24 hours with a free walkthrough or
                                    consult.
                                </p>
                            </div>
                            <div className="md:col-span-4 md:justify-self-end">
                                <Link
                                    to="/contact"
                                    data-testid="about-cta"
                                    className="btn-primary"
                                >
                                    Get a Quote <ArrowRight size={14} />
                                </Link>
                            </div>
                        </div>
                    </FadeUp>
                </div>
            </section>
        </main>
    );
}
